
import React, { useState } from "react";
import PersonaGrid from "@/components/PersonaGrid";
import { MemoryView } from "@/components/MemoryView";
import ChatInterface from "@/components/ChatInterface";
import VoiceAssistant from "@/components/VoiceAssistant";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { ArrowLeft, BookOpen, Maximize, Minimize } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

type Persona = "travel" | "health" | "meals" | "errands" | "home" | "work";

interface MainContentWithSidebarProps {
  selectedPersona: Persona | null;
  onPersonaSelect: (persona: Persona | null) => void;
  showAllMemories: boolean;
  onToggleAllMemories: () => void;
}

const personaDetails: Record<Persona, { name: string; description: string; emoji: string }> = {
  travel: { name: "Travel", description: "Flights, hotels, itineraries", emoji: "✈️" },
  health: { name: "Health", description: "Fitness, sleep, wellness", emoji: "❤️" },
  meals: { name: "Meals", description: "Meal planning, nutrition", emoji: "🍽️" },
  errands: { name: "Errands", description: "Appointments, calls, renewals", emoji: "🔔" },
  home: { name: "Home", description: "Bills, maintenance", emoji: "🏠" },
  work: { name: "Work", description: "Tasks, meetings, projects", emoji: "💼" },
};

const MainContentWithSidebar = ({ 
  selectedPersona, 
  onPersonaSelect, 
  showAllMemories, 
  onToggleAllMemories 
}: MainContentWithSidebarProps) => {
  const [showMemoryView, setShowMemoryView] = useState(false);
  const [activeTab, setActiveTab] = useState("chat");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const isMobile = useIsMobile();

  const handleFullscreenChange = (fullscreen: boolean) => {
    setIsFullscreen(fullscreen);
  };

  if (showAllMemories) {
    return (
      <div className="w-full h-full">
        <MemoryView 
          selectedPersona={null}
          onBack={onToggleAllMemories}
          showAllMemories={true}
        />
      </div>
    );
  }

  if (selectedPersona) {
    if (showMemoryView) {
      return (
        <div className="w-full h-full">
          <MemoryView 
            selectedPersona={selectedPersona}
            onBack={() => setShowMemoryView(false)}
            showAllMemories={false}
          />
        </div>
      );
    }

    const persona = personaDetails[selectedPersona];

    return (
      <Card className="bg-gray-900/50 dark:bg-gray-900/50 bg-white/90 border-gray-700 dark:border-gray-700 border-gray-200 backdrop-blur-sm w-full h-full flex flex-col">
        {!isFullscreen && (
          <CardHeader className={`${isMobile ? 'pb-2 px-3 pt-3' : 'pb-3 sm:pb-4 lg:pb-6 px-3 sm:px-4 lg:px-6'} flex-shrink-0`}>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                <Button
                  variant="ghost"
                  size={isMobile ? "sm" : "default"}
                  onClick={() => onPersonaSelect(null)}
                  className="text-gray-400 hover:text-white dark:text-gray-400 dark:hover:text-white text-gray-600 hover:text-gray-900 flex-shrink-0"
                >
                  <ArrowLeft className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                  <span className="text-xs sm:text-sm">Back</span>
                </Button>
                <div className="min-w-0 flex-1">
                  <CardTitle className={`${isMobile ? 'text-lg' : 'text-lg sm:text-xl lg:text-2xl'} font-bold text-white dark:text-white text-gray-900`}>
                    <span className="truncate">{persona.name} Assistant</span>
                  </CardTitle>
                  <p className={`${isMobile ? 'text-xs' : 'text-xs sm:text-sm lg:text-base'} text-gray-400 dark:text-gray-400 text-gray-600 truncate`}>
                    {persona.description}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size={isMobile ? "sm" : "default"}
                onClick={() => setShowMemoryView(true)}
                className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm flex-shrink-0 w-full sm:w-auto"
              >
                <BookOpen className="h-3 w-3 sm:h-4 sm:w-4" />
                <span>{isMobile ? "Memories" : "View Memories"}</span>
              </Button>
            </div>
          </CardHeader>
        )}
        <CardContent className={`${isMobile ? 'p-2 pt-0' : 'p-2 sm:p-3 lg:p-6'} flex-1 flex flex-col overflow-hidden`}>
          <Tabs defaultValue="chat" onValueChange={setActiveTab} className="w-full flex flex-col flex-1">
            {!isFullscreen && (
              <TabsList className={`grid w-full grid-cols-2 bg-gray-800 dark:bg-gray-800 bg-gray-100 ${isMobile ? 'h-8 mb-2' : 'h-9 sm:h-10 lg:h-11 mb-4'} flex-shrink-0`}>
                <TabsTrigger 
                  value="chat" 
                  className={`${isMobile ? 'text-xs' : 'text-xs sm:text-sm'} text-gray-300 dark:text-gray-300 text-gray-700 data-[state=active]:bg-primary data-[state=active]:text-white`}
                >
                  Chat
                </TabsTrigger>
                <TabsTrigger 
                  value="voice" 
                  className={`${isMobile ? 'text-xs' : 'text-xs sm:text-sm'} text-gray-300 dark:text-gray-300 text-gray-700 data-[state=active]:bg-primary data-[state=active]:text-white`}
                >
                  Voice
                </TabsTrigger>
              </TabsList>
            )}
            <TabsContent value="chat" className="flex-1 overflow-hidden">
              <ChatInterface 
                selectedPersona={selectedPersona} 
                onFullscreenChange={handleFullscreenChange}
              />
            </TabsContent>
            <TabsContent value="voice" className="flex-1">
              <div className="text-center h-full">
                <VoiceAssistant selectedPersona={selectedPersona} />
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    );
  }

  // Default view - show chat interface without persona selection
  return (
    <Card className="bg-gray-900/50 dark:bg-gray-900/50 bg-white/90 border-gray-700 dark:border-gray-700 border-gray-200 backdrop-blur-sm w-full h-full flex flex-col">
      {!isFullscreen && (
        <CardHeader className={`${isMobile ? 'pb-0 px-3 pt-2' : 'pb-0 sm:pb-1 lg:pb-1 px-3 sm:px-4 lg:px-6'} flex-shrink-0`}>
          <CardTitle className={`${isMobile ? 'text-base' : 'text-base sm:text-lg lg:text-xl'} font-bold text-white dark:text-white text-gray-900`}>
            {activeTab === "voice" ? "Speak with Your AI Assistant" : "Chat with Your AI Assistant"}
          </CardTitle>
        </CardHeader>
      )}
      <CardContent className={`${isMobile ? 'p-2 pt-0' : 'p-2 sm:p-2 lg:p-4'} flex-1 flex flex-col overflow-hidden`}>
        <Tabs defaultValue="chat" onValueChange={setActiveTab} className="w-full flex flex-col flex-1">
          {!isFullscreen && (
            <div className="flex items-center gap-2 mb-1 flex-shrink-0">
              <TabsList className={`grid grid-cols-2 bg-gray-800 dark:bg-gray-800 bg-gray-100 ${isMobile ? 'h-8' : 'h-9 sm:h-10 lg:h-11'} flex-1`}>
                <TabsTrigger 
                  value="chat" 
                  className={`${isMobile ? 'text-xs' : 'text-xs sm:text-sm'} text-gray-300 dark:text-gray-300 text-gray-700 data-[state=active]:bg-primary data-[state=active]:text-white`}
                >
                  Chat
                </TabsTrigger>
                <TabsTrigger 
                  value="voice" 
                  className={`${isMobile ? 'text-xs' : 'text-xs sm:text-sm'} text-gray-300 dark:text-gray-300 text-gray-700 data-[state=active]:bg-primary data-[state=active]:text-white`}
                >
                  Voice
                </TabsTrigger>
              </TabsList>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsFullscreen(!isFullscreen)}
                className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
              >
                {isFullscreen ? (
                  <>
                    <Minimize className="h-4 w-4 mr-1" />
                    <span className="text-xs">Minimize</span>
                  </>
                ) : (
                  <>
                    <Maximize className="h-4 w-4 mr-1" />
                    <span className="text-xs">Expand</span>
                  </>
                )}
              </Button>
            </div>
          )}
          <TabsContent value="chat" className="flex-1 overflow-hidden">
            <ChatInterface 
              selectedPersona={null} 
              onFullscreenChange={handleFullscreenChange}
            />
          </TabsContent>
          <TabsContent value="voice" className="flex-1">
            <div className="text-center h-full">
              <VoiceAssistant selectedPersona={null} />
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default MainContentWithSidebar;
